import React, { useState } from 'react';
import { Crown, Building2, Info, Check } from 'lucide-react';

/**
 * ModeSelectionModal - Allows students to choose between BLACKI and Non-BLACKI insights
 * Shown before AI insights generation
 */
const ModeSelectionModal = ({ isOpen, onSelect, onClose, studentCategory }) => {
  const [selectedMode, setSelectedMode] = useState(null);
  const [rememberPreference, setRememberPreference] = useState(false);

  if (!isOpen) return null;

  // Category-specific cutoff percentiles (from unified_cutoffs.json)
  const categoryCutoffs = {
    'General': { blacki: { sectional: 85, overall: 95 }, nonBlacki: { sectional: 75, overall: 95 } },
    'EWS': { blacki: { sectional: 85, overall: 95 }, nonBlacki: { sectional: 60, overall: 85 } },
    'NC-OBC': { blacki: { sectional: 80, overall: 90 }, nonBlacki: { sectional: 60, overall: 85 } },
    'SC': { blacki: { sectional: 65, overall: 70 }, nonBlacki: { sectional: 45, overall: 66 } },
    'ST': { blacki: { sectional: 55, overall: 65 }, nonBlacki: { sectional: 30, overall: 42 } },
    'PWD': { blacki: { sectional: 50, overall: 60 }, nonBlacki: { sectional: 30, overall: 42 } }
  };

  const cutoffs = categoryCutoffs[studentCategory] || categoryCutoffs['General'];

  const modes = [
    {
      key: 'BLACKI',
      icon: Crown,
      title: 'BLACKI Mode',
      subtitle: 'Premier IIMs',
      description: 'Bangalore, Lucknow, Ahmedabad, Calcutta, Kozhikode, Indore',
      cutoffInfo: 'Higher cutoffs • Aspirational targets',
      color: 'amber',
      bgGradient: 'from-amber-50 to-orange-50',
      borderColor: 'border-amber-200',
      iconBg: 'bg-amber-100',
      iconColor: 'text-amber-600'
    },
    {
      key: 'NON_BLACKI',
      icon: Building2,
      title: 'Non-BLACKI Mode',
      subtitle: 'Other Top IIMs',
      description: 'All other IIMs and premier B-schools',
      cutoffInfo: 'Moderate cutoffs • Achievable targets',
      color: 'blue',
      bgGradient: 'from-blue-50 to-indigo-50',
      borderColor: 'border-blue-200',
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600'
    }
  ];

  const handleContinue = () => {
    if (selectedMode) {
      onSelect(selectedMode, rememberPreference);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="max-w-2xl w-full bg-white rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-300">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-ims-primary to-ims-primary-dark px-6 py-5">
          <h2 className="text-xl font-bold text-white">
            Choose Your Target Institutes
          </h2>
          <p className="text-white/80 text-sm mt-1">
            We'll generate personalized insights based on your selection
          </p>
        </div>

        <div className="p-6">
          {/* Category Info */}
          {studentCategory && (
            <div className="flex items-center gap-2 bg-amber-50 border border-amber-100 rounded-lg px-4 py-2 mb-4">
              <span className="text-sm text-amber-800">
                <span className="font-medium">Category:</span> {studentCategory}
              </span>
            </div>
          )}

          {/* Info Banner */}
          <div className="flex items-start gap-3 bg-blue-50 border border-blue-100 rounded-lg p-4 mb-6">
            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Why two modes?</p>
              <p>
                BLACKI institutes (IIM B, L, A, C, K, I) have higher cutoffs. 
                Non-BLACKI includes other excellent IIMs with moderate cutoffs. 
                You can toggle between views anytime after generation.
              </p>
            </div>
          </div>

          {/* Mode Cards */}
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            {modes.map((mode) => {
              const Icon = mode.icon;
              const isSelected = selectedMode === mode.key;
              
              return (
                <button
                  key={mode.key}
                  onClick={() => setSelectedMode(mode.key)}
                  className={`relative text-left p-5 rounded-xl border-2 transition-all duration-200 hover:shadow-md ${
                    isSelected 
                      ? `${mode.borderColor} ${mode.bgGradient} ring-2 ring-offset-2 ring-${mode.color}-400` 
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}
                >
                  {/* Selection Indicator */}
                  {isSelected && (
                    <div className={`absolute top-3 right-3 w-6 h-6 rounded-full ${mode.iconBg} flex items-center justify-center`}>
                      <Check className={`w-4 h-4 ${mode.iconColor}`} />
                    </div>
                  )}

                  {/* Icon */}
                  <div className={`w-12 h-12 rounded-xl ${mode.iconBg} flex items-center justify-center mb-3`}>
                    <Icon className={`w-6 h-6 ${mode.iconColor}`} />
                  </div>

                  {/* Title */}
                  <h3 className="font-bold text-gray-900 mb-1">
                    {mode.title}
                  </h3>
                  <p className={`text-sm font-medium ${mode.iconColor} mb-2`}>
                    {mode.subtitle}
                  </p>

                  {/* Description */}
                  <p className="text-sm text-gray-600 mb-3">
                    {mode.description}
                  </p>

                  {/* Cutoff Info */}
                  <div className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${mode.iconBg} ${mode.iconColor}`}>
                    {mode.cutoffInfo}
                  </div>

                  {/* Category-Specific Cutoff Percentiles */}
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <p className="text-xs text-gray-500 mb-1">Required Percentiles ({studentCategory || 'General'}):</p>
                    <div className="flex gap-3 text-xs">
                      <span className="text-gray-700">
                        <span className="font-medium">Sectional:</span> {mode.key === 'BLACKI' ? cutoffs.blacki.sectional : cutoffs.nonBlacki.sectional}th
                      </span>
                      <span className="text-gray-700">
                        <span className="font-medium">Overall:</span> {mode.key === 'BLACKI' ? cutoffs.blacki.overall : cutoffs.nonBlacki.overall}th
                      </span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Remember Preference */}
          <label className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer transition-colors">
            <input
              type="checkbox"
              checked={rememberPreference}
              onChange={(e) => setRememberPreference(e.target.checked)}
              className="w-5 h-5 rounded border-gray-300 text-ims-primary focus:ring-ims-primary"
            />
            <span className="text-sm text-gray-700">
              Remember my choice for future reports
            </span>
          </label>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleContinue}
            disabled={!selectedMode}
            className={`px-6 py-2 rounded-lg text-sm font-medium transition-all ${
              selectedMode
                ? 'bg-ims-primary text-white hover:bg-ims-primary-dark shadow-sm'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModeSelectionModal;
